import { ApiError, type Lang, request, toRelativeMedia } from '@/api/client';

export const CMT_MAX = 600;
export const CMT_PAGE = 20;
export const MODERATION_MS = 3000;
export const DEMO_VIMEO_ID = '76979871';

export interface AudioMedia {
  url: string | null;
  duration: number;
}

export interface VideoChapter {
  at: number;
  label: string;
}

export interface VideoMedia {
  vimeoId: string;
  duration: number;
  title: string;
  teacher: string;
  chapters: VideoChapter[];
}

export interface ExplainMedia {
  audio: AudioMedia | null;
  video: VideoMedia | null;
}

export type CommentStatus = 'pending' | 'ok' | 'rejected';

export interface ExplainComment {
  id: string;
  author: string;
  teacher: boolean;
  mine: boolean;
  at: string | null;
  text: string;
  likes: number;
  liked: boolean;
  status: CommentStatus;
  reported?: boolean;
}

export interface CommentPage {
  items: ExplainComment[];
  hasMore: boolean;
  total: number;
}

export interface LikeResult {
  liked: boolean;
}

export interface ExplainApi {
  getMedia(questionId: string, lang: Lang): Promise<ExplainMedia>;
  prepareAudio(questionId: string): Promise<void>;
  getComments(questionId: string, page: number, lang: Lang): Promise<CommentPage>;
  postComment(
    questionId: string,
    text: string,
    author: string,
    lang: Lang,
  ): Promise<ExplainComment>;
  awaitModeration(questionId: string, commentId: string): Promise<ExplainComment>;
  likeComment(commentId: string): Promise<LikeResult>;
  deleteComment(commentId: string): Promise<void>;
  reportComment(commentId: string): Promise<void>;
}

export class CommentError extends Error {
  constructor(readonly code: 'empty' | 'too_long' | 'network') {
    super(code);
  }
}

export const commentRank = (c: ExplainComment) => (c.teacher && !c.mine ? 2 : 0) + (c.mine ? 1 : 0);

export const sortComments = (list: ExplainComment[]) =>
  list.slice().sort((a, b) => commentRank(b) - commentRank(a));

interface CommentsReply {
  data: ExplainComment[];
  count: number;
}

interface MediaReply {
  audio: AudioMedia | null;
}

function commentError(e: unknown): CommentError {
  if (e instanceof ApiError && e.status === 422) return new CommentError('too_long');
  return new CommentError('network');
}

export const explain: ExplainApi = {
  getMedia: (questionId, lang) =>
    request<MediaReply>(`/questions/${questionId}/media?lang=${lang}`).then((r) => ({
      audio: r.audio && { ...r.audio, url: toRelativeMedia(r.audio.url) },
      video: null,
    })),
  prepareAudio: () => Promise.resolve(),
  getComments: (questionId, page, lang) => {
    const skip = page * CMT_PAGE;
    return request<CommentsReply>(
      `/questions/${questionId}/comments?lang=${lang}&skip=${skip}&limit=${CMT_PAGE}`,
    ).then((r) => ({
      items: r.data,
      total: r.count,
      hasMore: skip + r.data.length < r.count,
    }));
  },
  postComment: (questionId, text, author, lang) => {
    if (!text.trim()) return Promise.reject(new CommentError('empty'));
    if (text.length > CMT_MAX) return Promise.reject(new CommentError('too_long'));
    return request<ExplainComment>(`/questions/${questionId}/comments?lang=${lang}`, {
      method: 'POST',
      body: JSON.stringify({ text, author: author || null }),
    }).catch((e: unknown) => {
      throw commentError(e);
    });
  },
  awaitModeration: () => Promise.reject(new Error('Comments are published without review')),
  likeComment: (commentId) =>
    request<LikeResult>(`/comments/${commentId}/like`, { method: 'POST' }),
  deleteComment: (commentId) =>
    request<void>(`/comments/${commentId}`, { method: 'DELETE' }).then(() => undefined),
  reportComment: (commentId) =>
    request<void>(`/comments/${commentId}/report`, { method: 'POST' }).then(() => undefined),
};
